import React from "react";
import { useTranslation } from "react-i18next";
import { CONSTANTS as C } from "config";
import OrderHourSetRow from "./OrderHourSetRow";

const days = [
  {
    id: "fri",
    label: "FRIDAY",
    enabled: C.KEY_FRI,
    start: C.KEY_FRI_START_TIME,
    end: C.KEY_FRI_END_TIME,
    start2: C.KEY_FRI_SLOT2_START_TIME,
    end2: C.KEY_FRI_SLOT2_END_TIME,
  },
  {
    id: "sat",
    label: "SATURDAY",
    enabled: C.KEY_SAT,
    start: C.KEY_SAT_START_TIME,
    end: C.KEY_SAT_END_TIME,
    start2: C.KEY_SAT_SLOT2_START_TIME,
    end2: C.KEY_SAT_SLOT2_END_TIME,
  },
  {
    id: "sun",
    label: "SUNDAY",
    enabled: C.KEY_SUN,
    start: C.KEY_SUN_START_TIME,
    end: C.KEY_SUN_END_TIME,
    start2: C.KEY_SUN_SLOT2_START_TIME,
    end2: C.KEY_SUN_SLOT2_END_TIME,
  },
  {
    id: "mon",
    label: "MONDAY",
    enabled: C.KEY_MON,
    start: C.KEY_MON_START_TIME,
    end: C.KEY_MON_END_TIME,
    start2: C.KEY_MON_SLOT2_START_TIME,
    end2: C.KEY_MON_SLOT2_END_TIME,
  },
  {
    id: "tue",
    label: "TUESDAY",
    enabled: C.KEY_TUE,
    start: C.KEY_TUE_START_TIME,
    end: C.KEY_TUE_END_TIME,
    start2: C.KEY_TUE_SLOT2_START_TIME,
    end2: C.KEY_TUE_SLOT2_END_TIME,
  },
  {
    id: "wed",
    label: "WEDNESDAY",
    enabled: C.KEY_WED,
    start: C.KEY_WED_START_TIME,
    end: C.KEY_WED_END_TIME,
    start2: C.KEY_WED_SLOT2_START_TIME,
    end2: C.KEY_WED_SLOT2_END_TIME,
  },
  {
    id: "thu",
    label: "THURSDAY",
    enabled: C.KEY_THU,
    start: C.KEY_THU_START_TIME,
    end: C.KEY_THU_END_TIME,
    start2: C.KEY_THU_SLOT2_START_TIME,
    end2: C.KEY_THU_SLOT2_END_TIME,
  },
];

const StoreHoursSection = ({
  store,
  setStore,
  hoursField = "workingHours",
}) => {
  const { t } = useTranslation();
  const [showSlot2, setShowSlot2] = React.useState({});

  React.useEffect(() => {
    const workingHours = store?.values?.[hoursField] || {};
    const derived = days.reduce((acc, day) => {
      const hasValidStart2 =
        typeof workingHours?.[day.start2] === "number" &&
        workingHours?.[day.start2] > 0;
      const hasValidEnd2 =
        typeof workingHours?.[day.end2] === "number" &&
        workingHours?.[day.end2] > 0;
      // Keep second slot hidden by default unless a real saved slot exists.
      acc[day.start2] = hasValidStart2 && hasValidEnd2;
      return acc;
    }, {});
    setShowSlot2(derived);
  }, [store?.values, hoursField]);

  const addSlot = (key) =>
    setShowSlot2((prev) => ({ ...prev, [key]: true }));

  const removeSlot = (start, end) => {
    setStore((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [hoursField]: {
          ...(prev.values[hoursField] || {}),
          [start]: null,
          [end]: null,
        },
      },
      errors: {
        ...prev.errors,
        [hoursField]: {
          ...(prev.errors[hoursField] || {}),
          [start]: null,
          [end]: null,
        },
      },
    }));

    setShowSlot2((prev) => ({ ...prev, [start]: false }));
  };

  const getError = (key) => store.errors?.[hoursField]?.[key];

  const handleStoreHoursSection = (key, val) => {
    const toTimestamp = (value) => {
      if (value == null || value === "") return null;
      if (typeof value === "number") return Number.isNaN(value) ? null : value;
      if (value?._isAMomentObject) return value.valueOf();
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? null : parsed;
    };

    setStore((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [hoursField]: {
          ...(prevState.values[hoursField] || {}),
          [key]: typeof val === "boolean" ? val : toTimestamp(val),
        },
      },
      errors: {
        ...prevState.errors,
        [hoursField]: {
          ...(prevState.errors[hoursField] || {}),
          [key]: "",
        },
      },
    }));
  };

  return (
    <div className="bg-white rounded-md p-4">
      <div className="w-full overflow-x-auto">
        <div className="min-w-[760px]">
          {/* Table Headers */}
          <div className="mb-3 flex w-full items-center gap-5">
            <div className="w-1/6 shrink-0 text-sm font-semibold text-gray-700">Day</div>
            <div className="w-1/4 shrink-0 text-sm font-semibold text-gray-700">From</div>
            <div className="w-1/4 shrink-0 text-sm font-semibold text-gray-700">To</div>
          </div>

          {/* Table Rows */}
          <div className="space-y-1">
            {days.map((day) => (
              <OrderHourSetRow
                key={day.id}
                label={t(day.label)}
                workingHours={store.values[hoursField] || {}}
                keyStartTime={day.start}
                keyEndTime={day.end}
                keyEnabled={day.enabled}
                keyStartTime2={day.start2}
                keyEndTime2={day.end2}
                showSlot2={showSlot2[day.start2]}
                addSlotInput={() => addSlot(day.start2)}
                removeSlotInput={() => removeSlot(day.start2, day.end2)}
                s1Error={getError(day.start)}
                s2Error={getError(day.start2)}
                overLapError={getError(day.end)}
                handleValues={handleStoreHoursSection}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreHoursSection;
